import { Entity } from '../entity.js';
import { EFFECT_TYPES } from '../combat/statusEffects.js';
import { abilityWorldScale, clampGroundCast, isInAbilityCircle, isInAbilityRing, isWithinAbilityRange } from '../combat/abilityArea.js';
import {
  VEIGAR_E_CC_DURATION_SEC,
  VEIGAR_E_DURATION_SEC,
  VEIGAR_E_EFFECT_DELAY_SEC,
  VEIGAR_E_INNER,
  VEIGAR_E_MANA,
  VEIGAR_E_OUTER,
  VEIGAR_E_RANGE,
  VEIGAR_Q_MANA,
  VEIGAR_Q_RADIUS,
  VEIGAR_Q_RANGE,
  VEIGAR_Q_SPEED,
  VEIGAR_W_MANA,
  VEIGAR_W_RADIUS,
  VEIGAR_W_RANGE
} from '../abilities/params/veigarParams.js';

export {
  VEIGAR_E_RANGE,
  VEIGAR_E_EFFECT_DELAY_SEC,
  VEIGAR_E_DURATION_SEC,
  VEIGAR_E_INNER,
  VEIGAR_E_OUTER,
  VEIGAR_E_CC_DURATION_SEC,
  VEIGAR_E_STUN_SEC
} from '../abilities/params/veigarParams.js';

export function createVeigar(id = 'veigar-1', team = 'blue', x = 300, y = 1300) {
  const veigar = new Entity({
    id,
    type: 'champion',
    team,
    x,
    y,
    maxHp: 525,
    maxMp: 490,
    ad: 52,
    ap: 0,
    armor: 24,
    mr: 30,
    ms: 340,
    attackRange: 550,
    attackSpeed: 0.625,
    name: 'Veigar',
    radius: 20
  });
  veigar.eventHorizon = null;
  return veigar;
}

export function castVeigarQ(caster, targetX, targetY, projectiles, gameTime) {
  if (!caster || caster.isDead || caster.mp < VEIGAR_Q_MANA) return false;
  caster.mp -= VEIGAR_Q_MANA;
  const angle = Math.atan2(targetY - caster.y, targetX - caster.x);
  const damage = 65 + (caster.ap || 0) * 0.6;
  projectiles.push({
    id: `veigar-q-${caster.id}-${gameTime}`,
    casterId: caster.id,
    team: caster.team,
    x: caster.x,
    y: caster.y,
    vx: Math.cos(angle) * VEIGAR_Q_SPEED,
    vy: Math.sin(angle) * VEIGAR_Q_SPEED,
    range: VEIGAR_Q_RANGE,
    traveled: 0,
    damage,
    damageType: 'magic',
    radius: VEIGAR_Q_RADIUS
  });
  return true;
}

export function castVeigarW(caster, x, y, entities = []) {
  if (!caster || caster.isDead || caster.mp < VEIGAR_W_MANA) return false;
  caster.mp -= VEIGAR_W_MANA;
  const aim = clampGroundCast(caster, x, y, VEIGAR_W_RANGE);
  const damage = 100 + (caster.ap || 0) * 0.75;
  for (const e of entities) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (!isInAbilityCircle(e, aim.x, aim.y, VEIGAR_W_RADIUS, caster)) continue;
    e.takeDamage(damage, caster, 'magic');
  }
  return true;
}

export function castVeigarE(caster, x, y) {
  if (!caster || caster.isDead || caster.mp < VEIGAR_E_MANA) return false;
  caster.mp -= VEIGAR_E_MANA;
  const aim = clampGroundCast(caster, x, y, VEIGAR_E_RANGE);
  const live = Number.isFinite(caster.liveWorldScale);
  caster.eventHorizon = {
    x: aim.x,
    y: aim.y,
    ...(live ? { z: aim.y } : {}),
    scale: abilityWorldScale(caster),
    useSandboxZ: live,
    armSec: VEIGAR_E_EFFECT_DELAY_SEC,
    remainingSec: VEIGAR_E_DURATION_SEC,
    hitIds: new Set()
  };
  return true;
}

function zoneProbe(entity, zone) {
  if (!zone?.useSandboxZ) return entity;
  return { x: entity.x, y: Number.isFinite(entity.z) ? entity.z : entity.y };
}

export function tickVeigarEventHorizon(caster, entities = [], dt = 0) {
  const zone = caster?.eventHorizon;
  if (!zone) return null;
  if (zone.armSec > 0) {
    zone.armSec -= dt;
    if (zone.armSec > 0) return zone;
  }
  zone.remainingSec -= dt;
  if (zone.remainingSec <= 0) {
    caster.eventHorizon = null;
    return null;
  }
  const scaleCaster = { liveWorldScale: zone.scale || 1 };
  const oy = Number.isFinite(zone.z) ? zone.z : zone.y;
  for (const e of entities) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (zone.hitIds.has(e.id)) continue;
    if (!isInAbilityRing(zoneProbe(e, zone), zone.x, oy, VEIGAR_E_INNER, VEIGAR_E_OUTER, scaleCaster)) {
      continue;
    }
    e.statusManager?.addEffect?.(EFFECT_TYPES.STUN, VEIGAR_E_CC_DURATION_SEC, 0);
    zone.hitIds.add(e.id);
  }
  return zone;
}

export function castVeigarR(caster, target) {
  if (!caster || caster.isDead || !target || target.isDead) return false;
  if (caster.mp < 100) return false;
  if (!isWithinAbilityRange(caster, target, 650, caster)) return false;
  caster.mp -= 100;
  const missing = target.maxHp - target.hp;
  const damage = 150 + (caster.ap || 0) * 0.75 + missing * 0.12;
  target.takeDamage(damage, caster, 'magic');
  return true;
}
